// src/pages/admin/AdminAnnouncements.jsx


import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  useAnnouncements,
  postAnnouncement,
  useAllUsers,
  formatDate,
} from "../../hooks/useFirestore";
import { db } from "../../firebase/config";
import { doc, updateDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { ADMIN_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import { useNotifications } from "../../context/NotificationContext";


const priorityIcon = { high: "🔴", medium: "🟡", low: "🟢" };

const emptyForm = {
  title: "",
  message: "",
  priority: "medium",
  audience: "all",
};

export default function AdminAnnouncements() {
  const { userProfile } = useAuth();
  const { data: announcements, loading } = useAnnouncements();
  const { data: users } = useAllUsers();
  const { addNotification } = useNotifications();

  // ── Create form state ─────────────────────────────────
  const [form, setForm]         = useState(emptyForm);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState("");

  // ── Edit state ────────────────────────────────────────
  const [editItem, setEditItem] = useState(null); // announcement doc
  const [editForm, setEditForm] = useState(emptyForm);
  const [editSaving, setEditSaving] = useState(false);
  const [editMsg, setEditMsg]   = useState("");

  // ── Delete state ──────────────────────────────────────
  const [deleting, setDeleting] = useState({});

  // ── Read-by panel ─────────────────────────────────────
  const [expandedId, setExpandedId] = useState(null);

  // ── Form helpers ──────────────────────────────────────
  function handleChange(e) {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  }


  // ── Create ────────────────────────────────────────────
  async function handlePost(e) {
    e.preventDefault();
    setSaving(true);
    setMsg("");
    try {
      await postAnnouncement({
  ...form,
  postedBy: userProfile.uid,
  postedByName: userProfile.displayName,
  teamId: null,
});
      
      await addNotification({
          type: "announcement",
          title: "New Announcement",
          message: `${userProfile.displayName} posted: ${form.title}`,
          recipientId: form.audience === "all" ? "all" : form.audience,
          link: "/employee/dashboard"
      });
      
      setMsg("✅ Announcement posted!");
      setForm(emptyForm);
    } catch (err) {
      setMsg("❌ " + err.message);
    }
    setSaving(false);
  }

  // ── Edit ──────────────────────────────────────────────
  function openEdit(a) {
    setEditItem(a);
    setEditForm({
      title:    a.title,
      message:  a.message,
      priority: a.priority,
      audience: a.audience,
    });
    setEditMsg("");
  }

  async function handleEditSave() {
    if (!editItem) return;
    setEditSaving(true);
    setEditMsg("");
    try {
      await updateDoc(doc(db, "announcements", editItem.id), {
        ...editForm,
        updatedAt: serverTimestamp(),
      });
      setEditMsg("✅ Updated successfully!");
      setTimeout(() => {
        setEditItem(null);
        setEditMsg("");
      }, 900);
    } catch (err) {
      setEditMsg("❌ " + err.message);
    }
    setEditSaving(false);
  }

  // ── Delete ────────────────────────────────────────────
  async function handleDelete(id) {
    if (!window.confirm("Delete this announcement?")) return;
    setDeleting((d) => ({ ...d, [id]: true }));
    try {
      await deleteDoc(doc(db, "announcements", id));
    } catch (err) {
      console.error(err);
    }
    setDeleting((d) => ({ ...d, [id]: false }));
  }

  // ── Read-by helpers ───────────────────────────────────
  function getReadStatus(announcement) {
    const readBy  = announcement.readBy || [];
    const readSet = new Set(readBy);
    const relevant = users.filter((u) => {
      if (announcement.audience === "all")       return u.role !== "admin";
      if (announcement.audience === "leads")     return u.role === "lead";
      if (announcement.audience === "employees") return u.role === "employee";
      if (announcement.audience === u.department) return true;
      if (announcement.audience === u.teamId)     return true;
      return false;
    });
    return {
      read:   relevant.filter((u) => readSet.has(u.uid)),
      unread: relevant.filter((u) => !readSet.has(u.uid)),
    };
  }

  return (
    <Layout navItems={ADMIN_NAV} panelLabel="Admin Panel">
      <div className="page-header">
        <h1 className="page-title">Announcements</h1>
      </div>

      <div className="two-col-layout">
        {/* ── Create form ── */}
        <div className="form-card">
          <h3 className="form-card-title">Create Announcement</h3>
          {msg && (
            <div className={msg.startsWith("✅") ? "form-success" : "form-error"}>
              {msg}
            </div>
          )}
          <form onSubmit={handlePost}>
            <div className="form-group">
              <label>Title *</label>
              <input
                name="title"
                value={form.title}
                onChange={handleChange}
                required
                placeholder="Important update"
              />
            </div>
            <div className="form-group">
              <label>Message *</label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                required
                rows={4}
                placeholder="Write your announcement…"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Priority</label>
                <select name="priority" value={form.priority} onChange={handleChange}>
                  <option value="high">🔴 High</option>
                  <option value="medium">🟡 Medium</option>
                  <option value="low">🟢 Low</option>
                </select>
              </div>
              <div className="form-group">
                <label>Send To</label>
                <select name="audience" value={form.audience} onChange={handleChange}>
                  <option value="all">All Employees</option>
                  <option value="leads">Team Leads</option>
                </select>
              </div>
            </div>

            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? "Posting…" : "Broadcast Announcement"}
            </button>
          </form>
        </div>

        {/* ── Announcements list ── */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">All Announcements</h3>
          </div>
          {loading ? (
            <div className="card-loading">Loading…</div>
          ) : announcements.length === 0 ? (
            <div className="card-empty">No announcements yet.</div>
          ) : (
            <div className="announcements-full-list">
              {announcements.map((a) => {
                const { read, unread } = getReadStatus(a);
                const isExpanded = expandedId === a.id;
                return (
                  <div key={a.id} className={`announcement-card priority-${a.priority}`}>
                    <div className="announcement-card-header">
                      <span>{priorityIcon[a.priority]} {a.title}</span>
                      <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                        <span className="announcement-audience">
                          {a.audience === "leads" ? "Team Leads" : a.audience === "employees" ? "Employees Only" : a.audience === "all" ? "All Employees" : a.audience}
                        </span>
                        <button
  className="ann-icon-btn"
  title="Edit"
  onClick={() => openEdit(a)}
>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-6"
    width="18"
    height="18"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
    />
  </svg>
</button>
                        <button
  className="ann-icon-btn ann-icon-btn--danger"
  title="Delete"
  onClick={() => handleDelete(a.id)}
  disabled={deleting[a.id]}
>
  {deleting[a.id] ? (
    "…"
  ) : (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="size-6"
      width="18"
      height="18"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
      />
    </svg>
  )}
</button>
                      </div>
                    </div>

                    <p className="announcement-card-msg">{a.message}</p>

                    

                    <div className="announcement-card-meta">
                      <span>By {a.postedByName}</span>
                      <span>{formatDate(a.createdAt)}</span>
                    </div>

                    {/* ── Read / Unread toggle ── */}
                    <button
                      className="ann-read-toggle"
                      onClick={() => setExpandedId(isExpanded ? null : a.id)}
                    >
                      <>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-6"
    width="18"
    height="18"
    style={{ verticalAlign: "middle", marginRight: "4px" }}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
    />
  </svg>
  {read.length} seen ·

  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-6"
    width="18"
    height="18"
    style={{ verticalAlign: "middle", marginLeft: "8px", marginRight: "4px" }}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
    />
  </svg>
  {unread.length} unseen
</>
                      <span style={{ marginLeft: "0.3rem" }}>{isExpanded ? "▲" : "▼"}</span>
                    </button>

                    {isExpanded && (
                      <div className="ann-read-panel">
                        <div className="ann-read-section">
                          <div className="ann-read-section-title">
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-6"
    width="18"
    height="18"
    style={{ verticalAlign: "middle", marginRight: "4px" }}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
    />
  </svg>
  Seen ({read.length})
</div>
                          {read.length === 0 ? (
                            <div className="ann-read-empty">No one has seen this yet.</div>
                          ) : read.map((u) => (
                            <div key={u.uid} className="ann-read-row ann-read-row--seen">
                              <span className="ann-read-avatar">
                                {u.displayName?.slice(0, 2).toUpperCase()}
                              </span>
                              <span className="ann-read-name">{u.displayName}</span>
                              <span className="ann-eye-icon">
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-6"
    width="18"
    height="18"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
    />
  </svg>
</span>
                            </div>
                          ))}
                        </div>
                        <div className="ann-read-section" style={{ marginTop: "0.75rem" }}>
                          <div className="ann-read-section-title">🚫 Not seen ({unread.length})</div>
                          {unread.length === 0 ? (
                            <div className="ann-read-empty">Everyone has seen this.</div>
                          ) : unread.map((u) => (
                            <div key={u.uid} className="ann-read-row ann-read-row--unseen">
                              <span className="ann-read-avatar">
                                {u.displayName?.slice(0, 2).toUpperCase()}
                              </span>
                              <span className="ann-read-name">{u.displayName}</span>
                              <span className="ann-eye-icon">🚫</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Edit Modal ── */}
      {editItem && (
        <div className="modal-overlay" onClick={() => setEditItem(null)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Edit Announcement</h2>
              <button className="modal-close" onClick={() => setEditItem(null)}>✕</button>
            </div>
            {editMsg && (
              <div className={editMsg.startsWith("✅") ? "form-success" : "form-error"}>
                {editMsg}
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Title *</label>
              <input
                className="form-input"
                value={editForm.title}
                onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Message *</label>
              <textarea
                className="form-input"
                rows={4}
                value={editForm.message}
                onChange={(e) => setEditForm((f) => ({ ...f, message: e.target.value }))}
              />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
              <div className="form-group">
                <label className="form-label">Priority</label>
                <select
                  className="form-select"
                  value={editForm.priority}
                  onChange={(e) => setEditForm((f) => ({ ...f, priority: e.target.value }))}
                >
                  <option value="high">🔴 High</option>
                  <option value="medium">🟡 Medium</option>
                  <option value="low">🟢 Low</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Audience</label>
                <select
                  className="form-select"
                  value={editForm.audience}
                  onChange={(e) => setEditForm((f) => ({ ...f, audience: e.target.value }))}
                >
                  <option value="all">All Employees</option>
                  <option value="leads">Team Leads</option>
                </select>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setEditItem(null)}>Cancel</button>
              <button className="btn-primary" onClick={handleEditSave} disabled={editSaving}>
                {editSaving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}